/**
 * amobler-variant-row.js
 *
 * Swaps variant-scoped metafield-table-row values when the customer
 * picks a different variant. Each row carries `data-amobler-variant-row`
 * and `data-amobler-variant-values="{json}"` where the JSON is a map of
 * `{variant_id: rendered_value}` for variants that have a non-blank value.
 * The visible value span inside the row carries `data-amobler-variant-value`.
 *
 * Triggered by the `variant:change:{sectionId}:{productId}` CustomEvent
 * dispatched by `<block-variant-picker>` (Expanse stock module, see
 * assets/block.product-variant-picker.js — dispatched with `bubbles: true`
 * and `detail.variant.id`). The event bubbles to document, so we listen
 * once at document level per (sectionId, productId). Listeners survive
 * theme-editor section re-renders for free: the event name is derived
 * from stable section/product IDs (Liquid `section.id` and `product.id`
 * don't change between renders), so the same name is re-dispatched by
 * the new picker and our document-level listener catches it.
 *
 * Rows that have no value for the currently-selected variant are
 * rendered with the `hidden` attribute and become visible only when a
 * variant with a value is picked.
 */
(function () {
  if (window.__amoblerVariantRowInit) return;
  window.__amoblerVariantRowInit = true;

  const ROW_SELECTOR = '[data-amobler-variant-row]';
  const VALUE_SELECTOR = '[data-amobler-variant-value]';

  function parseValues(row) {
    const raw = row.getAttribute('data-amobler-variant-values');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn('[amobler-variant-row] invalid JSON', err, raw);
      return null;
    }
  }

  function applyVariantId(variantId) {
    const id = String(variantId);
    document.querySelectorAll(ROW_SELECTOR).forEach((row) => {
      const values = parseValues(row);
      if (!values) return;
      const next = values[id];
      const valueEl = row.querySelector(VALUE_SELECTOR);
      if (next == null || next === '') {
        if (valueEl) valueEl.textContent = '';
        row.setAttribute('hidden', '');
      } else {
        if (valueEl) valueEl.textContent = next;
        row.removeAttribute('hidden');
      }
    });
  }

  // Register one document-level listener per (sectionId, productId) for
  // the picker's CustomEvent. Idempotent via a window-level key — calling
  // registerForPicker() repeatedly for the same picker is a no-op.
  function registerForPicker(picker) {
    const sectionId = picker.dataset.sectionId;
    const productId = picker.dataset.productId;
    if (!sectionId || !productId) return;
    const eventName = 'variant:change:' + sectionId + ':' + productId;
    const guardKey = '__amoblerVariantRowEvent:' + eventName;
    if (window[guardKey]) return;
    window[guardKey] = true;
    document.addEventListener(eventName, (event) => {
      const variantId = event.detail && event.detail.variant && event.detail.variant.id;
      if (variantId == null) return;
      applyVariantId(variantId);
    });
  }

  function init() {
    document
      .querySelectorAll('block-variant-picker[data-product-id]')
      .forEach(registerForPicker);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
